import { useEffect, useState } from 'react';
import { apiClient } from '../auth-lib';

// The sync dot beside "Tally" in the sidebar.
//
// ⚠️ A CONNECTOR THAT HAS QUIETLY STOPPED SYNCING IS THE FAILURE THAT MODULE
// EXISTS TO PREVENT, and until now the only place that said so was the Tally
// board itself — the one screen nobody opens precisely when everything looks
// fine. The sidebar is on every page, so this is where it costs nothing to say.
//
// ⚠️ MODULE-LEVEL CACHE, NOT COMPONENT STATE, AND THAT IS FORCED. Every page in
// the cockpit renders its own `<OrgAdminLayout>`, so the layout REMOUNTS on
// every navigation — a plain `useEffect` fetch would hit the server on each
// click. The same 15-second-cache reasoning `utils/planLimits.js` and
// `subscriptionGate.js` carry, with a longer TTL because "last synced" moves in
// minutes, not seconds.
const TTL_MS = 60_000;

let cache = null; // { at: epochMs, value }
let inFlight = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(cache?.value ?? null));

/** Throw the cache away — call after anything that changes a connection. */
export function invalidateTallyNavStatus() {
  cache = null;
  inFlight = null;
  // ⚠️ REFETCH, DO NOT MERELY FORGET. Dropping the cache alone corrects the
  // dot on the NEXT navigation — but the screen that just changed the number
  // is the one the user is standing on, and they have no reason to navigate.
  // The stale figure is exactly what this was meant to prevent.
  //
  // ⚠️ ONLY WHEN SOMETHING IS LISTENING. With no sidebar mounted there is
  // nothing to update, and a request per write from a headless caller is pure
  // waste.
  if (listeners.size) fetchStatus();
}

function fetchStatus() {
  // ⚠️ IN-FLIGHT DEDUPE. A navigation can mount the layout while the previous
  // request is still open; without this, clicking quickly through five pages
  // opens five identical requests and the last one to land wins.
  if (inFlight) return inFlight;

  inFlight = apiClient
    .get('/api/tally/nav-status')
    .then((res) => {
      cache = { at: Date.now(), value: res.data };
      notify();
      return res.data;
    })
    .catch(() => {
      // ⚠️ A FAILURE IS CACHED AS `null`, NOT RETRIED IN A LOOP. Somebody
      // without `nav.clients` gets a 403 here on every page; hammering it would
      // turn one missing permission into a request per navigation for ever. The
      // dot simply never appears, which is the same as this sidebar looked
      // before it had one.
      cache = { at: Date.now(), value: null };
      notify();
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * @param {boolean} enabled  false for a user who cannot see Tally at all — the
 *   hook then never fetches, rather than asking and being refused.
 */
export function useTallyNavStatus(enabled = true) {
  const [value, setValue] = useState(() => cache?.value ?? null);

  useEffect(() => {
    if (!enabled) return undefined;

    listeners.add(setValue);
    const fresh = cache && Date.now() - cache.at < TTL_MS;
    if (fresh) setValue(cache.value);
    else fetchStatus();

    return () => listeners.delete(setValue);
  }, [enabled]);

  return value;
}

/** "3 minutes ago" / "yesterday" — short enough for a tooltip. */
function agoLabel(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'never';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// How stale a successful sync has to be before the dot stops being green.
// Comfortably past the nightly window, so a firm that syncs at 2am is not amber
// all morning; short enough that two missed nights show.
const STALE_MS = 36 * 60 * 60 * 1000;

/**
 * Turn the raw counts into what the shell renders.
 *
 * ⚠️ NO CONNECTIONS MEANS NO DOT — a firm that has never set Tally up is not in
 * a bad state, and warning them about a feature they have not adopted is how an
 * indicator gets ignored by the people it is eventually for.
 */
export function tallyStatusDot(status) {
  if (!status || !status.connections) return null;

  const { connections, failing, inactive, lastSyncAt } = status;
  const synced = `Last synced ${agoLabel(lastSyncAt)}`;

  if (failing > 0) {
    return {
      tone: 'bad',
      title: `Tally: ${failing} of ${connections} connection${connections === 1 ? '' : 's'} failing. ${synced}`,
    };
  }
  // Never synced at all is amber rather than red: a connection bound five
  // minutes ago has not failed, it just has not run yet.
  if (!lastSyncAt || Date.now() - new Date(lastSyncAt).getTime() > STALE_MS) {
    return { tone: 'warn', title: `Tally: no successful sync recently. ${synced}` };
  }
  if (inactive > 0) {
    return {
      tone: 'warn',
      title: `Tally: ${inactive} of ${connections} connection${connections === 1 ? '' : 's'} paused or revoked. ${synced}`,
    };
  }
  return { tone: 'ok', title: `Tally: ${connections} connected. ${synced}` };
}
